import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import BandwidthPanelExportShell from './BandwidthPanelExportShell';
import { exportBandwidthCustomerSectionExcel } from './bandwidthDashboardExcelExport';
import { exportBandwidthCustomerSectionPdf } from './bandwidthDashboardPdfExport';
import {
  bwKpiPanelSubtitleClass,
  bwKpiTableBodyFirstColClassWithBg,
  bwKpiTableFooterRowClass,
  bwKpiTableHeaderCellClass,
  bwKpiTableHeaderClass,
  bwKpiTableHeaderFirstColClass,
  bwKpiTableSelectedRowClass,
  bwKpiTableZebraEven,
  bwKpiTableZebraOdd,
} from './bandwidthKpiTableChrome';
import { fmtBwAvg, fmtBwCount, fmtBwDecimal } from './bandwidthFormat';

export interface CountsByCustomerUserRow {
  userId: string;
  userName: string;
  totalActivity: number;
  timeHrs: number;
  avgHrsOnActivity: number | null;
  isInactiveUser?: boolean;
}

export interface CountsByCustomerRow {
  customer: string;
  customerId: string;
  /** Customer Type label: 'CSP' | 'Non CSP'. */
  customerType?: string | null;
  totalActivity: number;
  timeMinutes: number;
  timeHrs: number;
  avgHrsOnActivity: number | null;
  users: CountsByCustomerUserRow[];
}

export interface BandwidthCountsTableProps {
  title: string;
  subtitle?: string;
  rows: CountsByCustomerRow[];
  selectedCustomerId?: string | null;
  selectedUserIds?: string[] | null;
  onRowClick?: (customerId: string, customerLabel: string) => void;
  onUserClick?: (userId: string, userName: string) => void;
  loading?: boolean;
  errorMessage?: string | null;
  exportDateFrom: string;
  exportDateTo: string;
}

type SortKey = 'label' | 'count' | 'hours' | 'avgHrs';
type SortDir = 'asc' | 'desc';

function sortIndicator(active: boolean, dir: SortDir): string {
  if (!active) return '↕';
  return dir === 'desc' ? '↓' : '↑';
}

function ariaSortForColumn(active: boolean, dir: SortDir): 'ascending' | 'descending' | 'none' {
  if (!active) return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
}

const BandwidthCountsTable: React.FC<BandwidthCountsTableProps> = ({
  title,
  subtitle,
  rows,
  selectedCustomerId = null,
  selectedUserIds = null,
  onRowClick,
  onUserClick,
  loading = false,
  errorMessage = null,
  exportDateFrom,
  exportDateTo,
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sortKey, setSortKey] = useState<SortKey>('hours');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sortedRows = useMemo(() => {
    const dirMul = sortDir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      if (sortKey === 'label') {
        return dirMul * a.customer.localeCompare(b.customer, undefined, { sensitivity: 'base' });
      }
      if (sortKey === 'count') return dirMul * (a.totalActivity - b.totalActivity);
      if (sortKey === 'hours') return dirMul * (a.timeHrs - b.timeHrs);
      const avA = a.avgHrsOnActivity ?? -1;
      const avB = b.avgHrsOnActivity ?? -1;
      return dirMul * (avA - avB);
    });
  }, [rows, sortKey, sortDir]);

  const toggleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'label' ? 'asc' : 'desc');
    }
  }, [sortKey]);

  const sortHeadersDisabled = loading || Boolean(errorMessage) || rows.length === 0;

  const rowKey = useMemo(() => rows.map((r) => r.customerId).sort().join('|'), [rows]);

  useEffect(() => {
    setExpanded((prev) => {
      const next: Record<string, boolean> = { ...prev };
      for (const r of rows) {
        if (next[r.customerId] === undefined) next[r.customerId] = false;
      }
      return next;
    });
  }, [rowKey, rows]);

  useEffect(() => {
    if (!selectedUserIds?.length) return;
    setExpanded((prev) => {
      const next = { ...prev };
      for (const r of rows) {
        if (r.users.some((u) => selectedUserIds.includes(u.userId))) {
          next[r.customerId] = true;
        }
      }
      return next;
    });
  }, [selectedUserIds, rowKey, rows]);

  const toggleCustomer = useCallback((customerId: string) => {
    setExpanded((p) => ({ ...p, [customerId]: !p[customerId] }));
  }, []);

  const totals = rows.reduce(
    (acc, r) => ({
      count: acc.count + r.totalActivity,
      hrs: acc.hrs + r.timeHrs,
    }),
    { count: 0, hrs: 0 }
  );
  const totalAvgHrs = totals.count > 0 ? totals.hrs / totals.count : null;

  const exportDisabled = loading || Boolean(errorMessage) || rows.length === 0;

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      titleEnd={
        <BandwidthPanelExportShell
          disabled={exportDisabled}
          ariaLabel="Export customer breakdown"
          buildPdf={() => exportBandwidthCustomerSectionPdf(sortedRows, exportDateFrom, exportDateTo)}
          buildExcel={() => exportBandwidthCustomerSectionExcel(sortedRows, exportDateFrom, exportDateTo)}
        />
      }
      className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[440px]"
      flexFill
      bodyClassName="px-0 pb-0 pt-0"
    >
      {subtitle ? (
        <p className={bwKpiPanelSubtitleClass}>{subtitle}</p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full min-w-[280px] border-collapse text-left text-xs">
          <thead>
            <tr className={bwKpiTableHeaderClass}>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'label', sortDir)}
                className={`min-w-[120px] px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass} ${bwKpiTableHeaderFirstColClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('label')}
                  className="inline-flex items-center gap-1 rounded px-0.5 text-left font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Customer
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'label', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'count', sortDir)}
                className={`min-w-[100px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('count')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Total Activity Count
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'count', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'hours', sortDir)}
                className={`min-w-[100px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('hours')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Total Time Spent (Hrs)
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'hours', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'avgHrs', sortDir)}
                className={`min-w-[90px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('avgHrs')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Avg Hrs On Activity
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'avgHrs', sortDir)}
                  </span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="text-slate-800">
            {errorMessage ? (
              <tr>
                <td colSpan={4} className="px-3 py-10 text-center text-sm text-red-700">
                  {errorMessage}
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td colSpan={4} className="px-3 py-10 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-10 text-center text-slate-500">
                  No approved time entries for this filter.
                </td>
              </tr>
            ) : (
              <>
                {sortedRows.flatMap((r, ci) => {
                  const isOpen = expanded[r.customerId] === true;
                  const hasChildren = r.users.length > 0;
                  const customerSliceActive =
                    selectedCustomerId != null &&
                    r.customerId === selectedCustomerId &&
                    !selectedUserIds?.length;
                  const userSliceUnderThisCustomer =
                    selectedUserIds != null &&
                    selectedUserIds.some((uid) => r.users.some((u) => u.userId === uid));
                  const customerStripe = ci % 2 === 0 ? bwKpiTableZebraOdd : bwKpiTableZebraEven;

                  const customerRowClass = customerSliceActive
                    ? `${bwKpiTableSelectedRowClass} cursor-pointer`
                    : userSliceUnderThisCustomer
                      ? `${customerStripe} ring-1 ring-inset ring-blue-300/60`
                      : `${customerStripe} ${onRowClick ? 'cursor-pointer hover:bg-indigo-50/70' : ''}`;

                  const block: React.ReactNode[] = [
                    <tr key={`c-${r.customerId}`} className={`border-b border-slate-100 ${customerRowClass}`}
                      onClick={
                        onRowClick
                          ? () => {
                              onRowClick(
                                r.customerId,
                                r.customer
                              );
                            }
                          : undefined
                      }
                      onKeyDown={
                        onRowClick
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onRowClick(r.customerId, r.customer);
                              }
                            }
                          : undefined
                      }
                      tabIndex={onRowClick ? 0 : undefined}
                      role={onRowClick ? 'button' : undefined}
                    >
                      <td
                        className={`min-w-[120px] max-w-[220px] px-2 py-2 font-medium leading-snug text-slate-900 ${bwKpiTableBodyFirstColClassWithBg({ rowIndex: ci, selected: customerSliceActive })}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {hasChildren ? (
                            <button
                              type="button"
                              className="rounded p-0.5 text-slate-600 hover:bg-slate-200/80"
                              aria-expanded={isOpen}
                              aria-label={isOpen ? 'Collapse assigned users' : 'Expand assigned users'}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCustomer(r.customerId);
                              }}
                            >
                              {isOpen ? (
                                <ChevronDownIcon className="h-4 w-4 shrink-0" aria-hidden />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4 shrink-0" aria-hidden />
                              )}
                            </button>
                          ) : (
                            <span className="inline-block w-6 shrink-0" aria-hidden />
                          )}
                          <span>{r.customer}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtBwCount(r.totalActivity)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtBwDecimal(r.timeHrs)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtBwAvg(r.avgHrsOnActivity)}</td>
                    </tr>,
                  ];

                  if (isOpen && hasChildren) {
                    r.users.forEach((u, ui) => {
                      const userSelected =
                        selectedUserIds != null && selectedUserIds.includes(u.userId);
                      const stripe =
                        (ci + ui + 1) % 2 === 0 ? bwKpiTableZebraOdd : bwKpiTableZebraEven;
                      block.push(
                        <tr
                          key={`u-${r.customerId}-${u.userId}`}
                          className={`border-b border-slate-100 ${
                            userSelected
                              ? `${bwKpiTableSelectedRowClass} cursor-pointer`
                              : `${stripe} ${onUserClick ? 'cursor-pointer hover:bg-indigo-50/70' : ''}`
                          }`}
                          onClick={
                            onUserClick
                              ? () => {
                                  onUserClick(u.userId, u.userName);
                                }
                              : undefined
                          }
                          onKeyDown={
                            onUserClick
                              ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onUserClick(u.userId, u.userName);
                                  }
                                }
                              : undefined
                          }
                          tabIndex={onUserClick ? 0 : undefined}
                          role={onUserClick ? 'button' : undefined}
                        >
                          <td
                            className={`min-w-[120px] pl-9 pr-3 py-1.5 font-medium text-slate-700 ${bwKpiTableBodyFirstColClassWithBg({ rowIndex: ci + ui + 1, selected: userSelected })}`}
                          >
                            {u.userName}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmtBwCount(u.totalActivity)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmtBwDecimal(u.timeHrs)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmtBwAvg(u.avgHrsOnActivity)}</td>
                        </tr>
                      );
                    });
                  }

                  return block;
                })}
                <tr className={bwKpiTableFooterRowClass}>
                  <td className={`px-3 py-2.5 ${bwKpiTableBodyFirstColClassWithBg({ footer: true })}`}>Total</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtBwCount(totals.count)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtBwDecimal(totals.hrs)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {totalAvgHrs != null ? fmtBwAvg(totalAvgHrs) : '—'}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </KpiChartPanel>
  );
};

export default BandwidthCountsTable;
